const { groupForecastRows } = require('../tools/read/to-forecast-per-location')

const stripProductPrefix = (sku) =>
  typeof sku === 'string' && sku.startsWith('product:')
    ? sku.replace('product:', '')
    : sku

const addProductPrefix = (sku) =>
  typeof sku === 'string' && !sku.startsWith('product:')
    ? `product:${sku}`
    : sku

class ForecastDataAdapter {
  constructor (pgConnection) {
    this.pgConnection = pgConnection
  }

  async get (locationId, program, { sku } = {}) {
    if (!locationId) throw new Error('locationId is required')
    if (!program) throw new Error('program is required')

    const params = [locationId, program]
    const skus = sku ? (Array.isArray(sku) ? sku : [sku]).map(stripProductPrefix) : []
    let skuFilter = ''
    if (skus.length > 0) {
      skuFilter = `AND f.sku = ANY($${params.push(skus)}::text[])`
    }

    const { rows } = await this.pgConnection.query(`
      SELECT DISTINCT ON (f.sku)
        f.sku,
        f.daily_forecast,
        f.safety_stock,
        sp.supply_period_days,
        sp.lead_time_days,
        f.safety_stock AS min_level,
        f.safety_stock + f.daily_forecast * sp.lead_time_days AS reorder_point,
        f.safety_stock + f.daily_forecast * sp.supply_period_days AS max_level
      FROM avocado.data_forecast f
      JOIN avocado.data_location l ON f.location = l.uuid
      JOIN avocado.data_program p ON f.program = p.id
      JOIN LATERAL (
        SELECT supply_period_days, lead_time_days
        FROM avocado.data_supply_plan_params
        WHERE program = f.program
          AND sku = f.sku
          AND location_type = l.supply_chain_level
        ORDER BY date DESC
        LIMIT 1
      ) sp ON true
      WHERE l.fsid = $1
        AND p.fsid = $2
        ${skuFilter}
      ORDER BY f.sku, f.created_at DESC
    `, params)

    return rows.reduce((acc, row) => {
      const productId = addProductPrefix(row.sku)
      acc.products[productId] = {
        productId,
        dailyDemand: Number(row.daily_forecast),
        safetyStock: Number(row.safety_stock),
        supplyPeriodDays: Number(row.supply_period_days),
        leadTimeDays: Number(row.lead_time_days),
        minLevel: Number(row.min_level),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, { locationId, products: {} })
  }

  // Returns aggregated forecast thresholds per SKU across all locations for the given program.
  // safety_stock, reorder_point and max_level are summed across all warehouse locations.
  // When consumptionLocationId is provided, dailyDemand is scoped to that location's latest
  // daily_forecast only, used to display AMC for a specific location on the national view.
  // When locationIds (fsids) are provided, the aggregate is limited to those locations.
  // Note both cannot be combined meaningfully: consumptionLocationId overrides dailyDemand
  // with a single location's forecast, discarding the group sum.
  async getAggregated (program, consumptionLocationId = null, locationIds = null, productIds = null) {
    if (!program) throw new Error('program is required')

    const params = [program, consumptionLocationId]
    let locationFilter = ''
    if (Array.isArray(locationIds) && locationIds.length > 0) {
      locationFilter = `
        AND location IN (
          SELECT uuid FROM avocado.data_location WHERE fsid = ANY($${params.push(locationIds)}::text[])
        )`
    }

    const skus = productIds ? (Array.isArray(productIds) ? productIds : [productIds]).map(stripProductPrefix) : []
    let skuFilter = ''
    if (skus.length > 0) {
      skuFilter = `
        AND sku = ANY($${params.push(skus)}::text[])`
    }

    const { rows } = await this.pgConnection.query(`
      SELECT
        f.sku,
        SUM(f.daily_forecast)                                             AS daily_forecast,
        SUM(f.safety_stock)                                              AS safety_stock,
        SUM(f.safety_stock + f.daily_forecast * sp.lead_time_days)       AS reorder_point,
        SUM(f.safety_stock + f.daily_forecast * sp.supply_period_days)   AS max_level,
        loc_f.daily_forecast                                              AS location_daily_forecast,
        loc_sp.lead_time_days                                             AS location_lead_time_days
      FROM (
        SELECT DISTINCT ON (location, program, sku) *
        FROM avocado.data_forecast
        WHERE program = (SELECT id FROM avocado.data_program WHERE fsid = $1)
          ${locationFilter}
          ${skuFilter}
        ORDER BY location, program, sku, created_at DESC
      ) f
      JOIN avocado.data_location l ON f.location = l.uuid
      JOIN LATERAL (
        SELECT supply_period_days, lead_time_days
        FROM avocado.data_supply_plan_params
        WHERE program = f.program
          AND sku = f.sku
          AND location_type = l.supply_chain_level
        ORDER BY date DESC
        LIMIT 1
      ) sp ON true
      LEFT JOIN (
        SELECT DISTINCT ON (sku) sku, daily_forecast
        FROM avocado.data_forecast
        WHERE program = (SELECT id FROM avocado.data_program WHERE fsid = $1)
          AND location = (SELECT uuid FROM avocado.data_location WHERE fsid = $2)
          ${skuFilter}
        ORDER BY sku, created_at DESC
      ) loc_f ON f.sku = loc_f.sku
      LEFT JOIN (
        SELECT DISTINCT ON (lsp.sku) lsp.sku, lsp.lead_time_days
        FROM avocado.data_supply_plan_params lsp
        JOIN avocado.data_location hl
          ON hl.fsid = $2 AND hl.supply_chain_level = lsp.location_type
        WHERE lsp.program = (SELECT id FROM avocado.data_program WHERE fsid = $1)
          ${skuFilter}
        ORDER BY lsp.sku, lsp.date DESC
      ) loc_sp ON f.sku = loc_sp.sku
      GROUP BY f.sku, loc_f.daily_forecast, loc_sp.lead_time_days
    `, params)

    return rows.reduce((acc, row) => {
      acc[addProductPrefix(row.sku)] = {
        dailyDemand: row.location_daily_forecast !== null ? Number(row.location_daily_forecast) : Number(row.daily_forecast),
        leadTimeDays: row.location_lead_time_days !== null ? Number(row.location_lead_time_days) : undefined,
        minLevel: Number(row.safety_stock),
        reorderPoint: Number(row.reorder_point),
        maxLevel: Number(row.max_level)
      }
      return acc
    }, {})
  }

  async getLatestExport (organisationId, locationFsid = null) {
    const condition = locationFsid === null ? 'location_fsid is null' : 'location_fsid = $2'
    const params = locationFsid === null ? [organisationId] : [organisationId, locationFsid]
    const { rows: [exportFile] } = await this.pgConnection.query(`
      select
        id,
        bucket,
        key,
        expires_at as "expiresAt"
      from avocado.analytics_forecast_s3_exports
      where organisation_id = $1
        and ${condition}
        and expires_at > now()
      order by created_at desc
      limit 1
    `, params)
    return exportFile
  }

  async list ({ locationIds, program, sku } = {}) {
    const conditions = []
    const params = []
    if (locationIds && locationIds.length > 0) {
      // Handle the case where a single locationId is passed as a string instead of an array
      const ids = Array.isArray(locationIds) ? locationIds : [locationIds]
      conditions.push(`location = ANY($${params.push(ids)}::text[])`)
    }
    if (program) {
      conditions.push(`program = $${params.push(program)}`)
    }
    if (sku) {
      conditions.push(`sku = $${params.push(stripProductPrefix(sku))}`)
    }
    const where = conditions.length > 0 ? `WHERE ${conditions.join(' AND ')}` : ''
    const { rows } = await this.pgConnection.query(`
      SELECT * FROM avocado.vw_supply_plan_values ${where}
    `, params)
    const prefixedRows = rows.map(row => ({ ...row, sku: addProductPrefix(row.sku) }))
    return groupForecastRows(prefixedRows)
  }
}

module.exports = ForecastDataAdapter
